import { useState } from 'react';
import { Box, Typography, Tooltip, Button } from '@mui/material';
import {
    ExpandMore,
    InsertDriveFileOutlined,
    HighlightOff,
    AttachMoney,
    AccessTime,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { Severity } from 'hooks/analytics';
import { AnalyticsColors } from './analyticsUi';

const INITIAL_VISIBLE = 5;

const severityConfig = {
    red: {
        bg: '#FEF2F2',
        border: '#FECACA',
        iconBg: '#DC2626',
        titleColor: '#991B1B',
        accentBar: '#DC2626',
    },
    amber: {
        bg: '#FFFBEB',
        border: '#FDE68A',
        iconBg: '#D97706',
        titleColor: '#92400E',
        accentBar: '#D97706',
    },
    green: {
        bg: '#F0FDF4',
        border: '#BBF7D0',
        iconBg: '#059669',
        titleColor: '#166534',
        accentBar: '#059669',
    },
};

type AlertIcon = 'document' | 'fix' | 'payment' | 'offer';

const iconMap: Record<AlertIcon, SvgIconComponent> = {
    document: InsertDriveFileOutlined,
    fix: HighlightOff,
    payment: AttachMoney,
    offer: AccessTime,
};

export interface AlertDetailItem {
    label: React.ReactNode;
    value: string;
    actions?: React.ReactNode;
}

export interface AlertDetailGroup {
    groupLabel: React.ReactNode;
    groupKey: string;
    itemCount: number;
    items: AlertDetailItem[];
    groupActions?: React.ReactNode;
}

interface AlertCardProps {
    severity: Severity;
    icon: AlertIcon;
    title: string;
    subtitle?: string;
    count: number;
    tooltip?: string;
    details?: AlertDetailItem[];
    groups?: AlertDetailGroup[];
}

const AlertCard = ({
    severity,
    icon,
    title,
    subtitle,
    count,
    tooltip,
    details,
    groups,
}: AlertCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const IconComponent = iconMap[icon];
    const cfg = severityConfig[severity];

    const hasDetails = (details && details.length > 0) || (groups && groups.length > 0);

    const toggleGroup = (key: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const detailRowSx = (indented?: boolean) => ({
        display: 'flex',
        alignItems: 'center',
        p: indented ? '8px 16px 8px 40px' : '10px 16px 10px 16px',
        fontSize: 13,
        borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
        gap: '12px',
        transition: 'background 0.1s',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.8)',
        },
        '&:last-child': {
            borderBottom: 'none',
        },
    });

    const renderGrouped = () => {
        if (!groups) return null;
        const visibleGroups = showAll ? groups : groups.slice(0, INITIAL_VISIBLE);

        return (
            <>
                {visibleGroups.map((group) => {
                    const isGroupExpanded = expandedGroups.has(group.groupKey);
                    return (
                        <div key={group.groupKey}>
                            <Box
                                onClick={() => toggleGroup(group.groupKey)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: '10px 16px 6px 16px',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    background: 'rgba(255, 255, 255, 0.4)',
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.8)',
                                    },
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: AnalyticsColors.textTertiary,
                                        transition: 'transform 0.2s ease',
                                        transform: isGroupExpanded ? 'rotate(180deg)' : 'rotate(0)',
                                        flexShrink: 0,
                                    }}
                                >
                                    <ExpandMore sx={{ fontSize: 14 }} />
                                </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: AnalyticsColors.textPrimary,
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {group.groupLabel}
                                </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        px: '8px',
                                        py: '2px',
                                        borderRadius: '10px',
                                        background: 'rgba(0, 0, 0, 0.06)',
                                        color: AnalyticsColors.textSecondary,
                                        flexShrink: 0,
                                    }}
                                >
                                    {group.itemCount} {group.itemCount === 1 ? 'item' : 'items'}
                                </Box>
                                {group.groupActions && (
                                    <Box sx={{ flexShrink: 0 }}>{group.groupActions}</Box>
                                )}
                            </Box>
                            {isGroupExpanded &&
                                group.items.map((item, j) => (
                                    <Box key={j} sx={detailRowSx(true)}>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontWeight: 500,
                                                color: AnalyticsColors.textPrimary,
                                                minWidth: 0,
                                                flex: 1,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {item.label}
                                        </Box>
                                        <Box
                                            component="span"
                                            sx={{
                                                color: AnalyticsColors.textSecondary,
                                                fontSize: 12,
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {item.value}
                                        </Box>
                                        {item.actions && (
                                            <Box sx={{ flexShrink: 0 }}>{item.actions}</Box>
                                        )}
                                    </Box>
                                ))}
                        </div>
                    );
                })}
                {groups.length > INITIAL_VISIBLE && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            p: '8px 16px',
                            borderTop: '1px solid rgba(0, 0, 0, 0.04)',
                        }}
                    >
                        <Button
                            size="small"
                            onClick={() => setShowAll(!showAll)}
                            sx={{
                                textTransform: 'none',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: AnalyticsColors.primary,
                            }}
                        >
                            {showAll ? 'Show less' : `Show all ${groups.length} clients`}
                        </Button>
                    </Box>
                )}
            </>
        );
    };

    const renderFlat = () => {
        if (!details) return null;
        const visibleItems = showAll ? details : details.slice(0, INITIAL_VISIBLE);

        return (
            <>
                {visibleItems.map((d, i) => (
                    <Box key={i} sx={detailRowSx()}>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 500,
                                color: AnalyticsColors.textPrimary,
                                minWidth: 0,
                                flex: 1,
                                lineHeight: 1.4,
                            }}
                        >
                            {d.label}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                color: AnalyticsColors.textSecondary,
                                fontSize: 12,
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            {d.value}
                        </Box>
                        {d.actions && <Box sx={{ flexShrink: 0 }}>{d.actions}</Box>}
                    </Box>
                ))}
                {details.length > INITIAL_VISIBLE && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            p: '8px 16px',
                            borderTop: '1px solid rgba(0, 0, 0, 0.04)',
                        }}
                    >
                        <Button
                            size="small"
                            onClick={() => setShowAll(!showAll)}
                            sx={{
                                textTransform: 'none',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: AnalyticsColors.primary,
                            }}
                        >
                            {showAll ? 'Show less' : `Show all ${details.length} items`}
                        </Button>
                    </Box>
                )}
            </>
        );
    };

    const card = (
        <Box
            sx={{
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                borderLeft: `4px solid ${cfg.accentBar}`,
                borderRadius: '8px',
                overflow: 'hidden',
                transition: 'box-shadow 0.15s ease',
                '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                },
            }}
        >
            <Box
                onClick={() => hasDetails && setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    p: '14px 16px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        background: cfg.iconBg,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <IconComponent sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: cfg.titleColor,
                            lineHeight: 1.4,
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: AnalyticsColors.textSecondary,
                                mt: '2px',
                                lineHeight: 1.4,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 32,
                        height: 32,
                        px: '10px',
                        borderRadius: '8px',
                        fontSize: 16,
                        fontWeight: 700,
                        color: cfg.titleColor,
                        background: 'white',
                        border: `1px solid ${cfg.border}`,
                        flexShrink: 0,
                    }}
                >
                    {count}
                </Box>
                {hasDetails && (
                    <Box
                        component="span"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: AnalyticsColors.textTertiary,
                            transition: 'transform 0.2s ease',
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
                            flexShrink: 0,
                            ml: '4px',
                        }}
                    >
                        <ExpandMore sx={{ fontSize: 14 }} />
                    </Box>
                )}
            </Box>
            {expanded && hasDetails && (
                <Box
                    sx={{
                        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                        background: 'rgba(255, 255, 255, 0.6)',
                        maxHeight: 400,
                        overflowY: 'auto',
                    }}
                >
                    {groups ? renderGrouped() : renderFlat()}
                </Box>
            )}
        </Box>
    );

    if (tooltip) {
        return (
            <Tooltip title={tooltip} arrow placement="top">
                <div>{card}</div>
            </Tooltip>
        );
    }
    return card;
};

export default AlertCard;
